/*
 * The subscribe / one-time control.
 *
 * It swaps which server-rendered amount is visible and what the form posts. It
 * does NOT calculate a price: both amounts come from the selling plan
 * allocation in Liquid, so a plan change in Recharge cannot leave a stale
 * number here. See the header of snippets/ap-subscribe-toggle.liquid.
 *
 * Subscribe is the default because the markup ships that way, so the page is
 * correct before this file runs and stays correct if it never does. Without
 * JavaScript the form posts the selling plan it was rendered with, which is a
 * working subscription rather than a broken control -- the noscript note says
 * so.
 */
class ApSubscribeToggle extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-ap-subscribe-input]');
    this.amount = this.querySelector('[data-ap-subscribe-amount]');
    this.saving = this.querySelector('[data-ap-subscribe-saving]');
    this.compare = this.querySelectorAll('[data-ap-subscribe-compare], [data-ap-subscribe-compare-label]');
    this.options = Array.from(this.querySelectorAll('[data-ap-subscribe-option]'));
    this.termsSubscribe = this.querySelector('[data-ap-subscribe-terms-subscribe]');
    this.termsOnetime = this.querySelector('[data-ap-subscribe-terms-onetime]');

    const read = (sel) => this.querySelector(sel)?.textContent?.trim() ?? '';
    this.money = {
      subscribe: read('[data-ap-subscribe-money-subscribe]'),
      onetime: read('[data-ap-subscribe-money-onetime]'),
    };
    this.planId = this.dataset.planId || '';

    if (!this.input || !this.amount || this.options.length !== 2) return;

    this.options.forEach((btn) => {
      btn.addEventListener('click', () => this.select(btn.dataset.apSubscribeOption));
      btn.addEventListener('keydown', (e) => this.onKeydown(e));
    });
  }

  /* Arrow keys move between radios in a radiogroup; Home and End are the
     documented shorthands for first and last. Two options makes both pairs
     equivalent, which is fine -- the behaviour is what a screen reader user
     expects, not the shortest code. */
  onKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const back = event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'Home';
    const next = this.options[back ? 0 : 1];
    this.select(next.dataset.apSubscribeOption);
    next.focus();
  }

  select(mode) {
    const subscribing = mode === 'subscribe';

    /* An EMPTY value, not a removed field. Shopify reads an empty selling_plan
       as a plain purchase, so the field can stay in the form and the control
       never has to rebuild it -- which also means nothing to get wrong if the
       variant picker re-renders around it. */
    this.input.value = subscribing ? this.planId : '';

    this.amount.textContent = subscribing ? this.money.subscribe : this.money.onetime;
    if (this.saving) this.saving.hidden = !subscribing;
    /* The bundle's sum-of-parts comparison belongs to the one-time price.
       Beside the subscription amount it would read as the plan discounting
       off it, which is the markdown framing standing decision 0 rejects. */
    this.compare.forEach((el) => { el.hidden = subscribing; });
    if (this.termsSubscribe) this.termsSubscribe.hidden = !subscribing;
    if (this.termsOnetime) this.termsOnetime.hidden = subscribing;

    this.options.forEach((btn) => {
      const on = btn.dataset.apSubscribeOption === mode;
      btn.classList.toggle('ap-subscribe__option--on', on);
      btn.setAttribute('aria-checked', String(on));
    });
  }
}

if (!customElements.get('ap-subscribe-toggle')) {
  customElements.define('ap-subscribe-toggle', ApSubscribeToggle);
}
